import {
  IOrderSummary,
  IOrderSummaryLineItem,
  IOrderTicket,
  ITicketDescriptionEntry,
  ITicketDescriptionFields,
  ITicketProductWithBundleType,
} from '@nx-ng/types';
import { Applicant, Order, OrderLine } from '../client/src';
import { KnownApplicantFulfillmentMethod } from '@modules/checkout-store';
import { sumBy } from 'lodash-es';

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'June',
  'July',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

const dateSuffixMap: {
  [int: number]: string;
} = {
  1: 'st',
  2: 'nd',
  3: 'rd',
  21: 'st',
  22: 'nd',
  23: 'rd',
  31: 'st',
};

export const mapOrderToOrderSummary = ({
  order,
  fareIdsAndDescriptions,
}: {
  order?: Order;
  fareIdsAndDescriptions?: ITicketProductWithBundleType[];
}): IOrderSummary => {
  return {
    orderNumber: order?.orderNumber || '',
    orderDate: formatOrderDate(order?.orderDate),
    orderSubtotal: sumBy(order?.orderLines, (line) => line.subTotalPrice),
    orderTotal: order?.orderTotal || 0,
    orderLines: mapOrderLines({
      orderLines: order?.orderLines,
      fareIdsAndDescriptions,
    }),
    membershipNumber: order?.customer?.id || '',
    discount: order?.discount ? {
      label: order.discount.discountLabel,
      discountValue: order.discount.totalDiscountValue
    } : undefined
  };
};

export const mapOrderLines = ({
  orderLines,
  fareIdsAndDescriptions,
}: {
  orderLines?: OrderLine[];
  fareIdsAndDescriptions?: ITicketProductWithBundleType[];
}): IOrderSummaryLineItem[] => {
  if (!orderLines) {
    return [];
  }

  return orderLines.flatMap((orderLine: OrderLine) => {
    const ticket = fareIdsAndDescriptions?.find(
      (ticket: ITicketProductWithBundleType) =>
        ticket.id === Number(orderLine.itemId)
    );

    const summaryItems: IOrderSummaryLineItem[] = [];

    for (let quantityIndex = 1; quantityIndex <= orderLine.quantity; quantityIndex++) {
      summaryItems.push({
        cost: orderLine.itemPrice,
        label: ticket?.description || '',
      })
    }

    return summaryItems;
  });
};

export const mapOrderToOrderTicketList = ({
  order,
  fareIdsAndDescriptions,
}: {
  order?: Order;
  fareIdsAndDescriptions?: ITicketDescriptionFields;
}): IOrderTicket[] => {
  if (!order || !order.orderLines) {
    return [];
  }

  // One order line per ticket quantity
  return order.orderLines.flatMap((orderLine: OrderLine): IOrderTicket[] => {
    const fare = fareIdsAndDescriptions?.[Number(orderLine.itemId)];

    const alias = fare?.alias || '';
    const applicantLines = orderLine.applicants.map((applicant): IOrderTicket => {
      return {
        membershipNumber: applicant.customer?.id || '',
        title: fare?.title || '',
        isDirectDebit: fare?.isDirectDebit || false,
        ticketTypeId: orderLine.ticketTypeId.toString(),
        description: fare?.description || '',
        price: orderLine.itemPrice,
        customerType: fare?.customerType || '',
        travelMethod: fare?.travelMethod || '',
        travelTime: fare?.travelTime || '',
        zone: fare?.zone || '',
        alias: alias,
        subtitle: `${alias}-subtitle`,
        fullName: formatApplicantName(applicant),
        validFrom: formatStartDate(applicant?.startDate, fare),
        validTo: formatEndDate(applicant?.startDate, fare),
        fullfillmentMethod: applicant?.fulfillmentMethod as KnownApplicantFulfillmentMethod,
        quantity: orderLine?.quantity || 0,
        id: orderLine?.itemId.toString() || '',
      };
    });

    return applicantLines;
  });
};

export const addDaysToDate = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const fetchTicketIdsFromOrder = (order?: Order): string[] => {
  if (!order || !order.orderLines) {
    return [];
  }

  return order.orderLines.map((orderLine: OrderLine) =>
    orderLine.itemId.toString()
  );
};

export const formatApplicantName = (applicant?: Applicant) => {
  if (!applicant || !applicant.customer) {
    return '';
  }

  const { firstName, lastName } = applicant.customer;
  return `${firstName} ${lastName}`;
};

export const formatApplicantMembershipNumber = (applicant?: Applicant) => {
  if (!applicant || !applicant.customer) {
    return '';
  }

  return applicant.customer.id; // TODO: confrm what membership number will be
};

export const formatOrderDate = (orderDateIso?: string): string => {
  if (!orderDateIso) {
    return '';
  }

  const timePart = formatOrderDate_TimePart(orderDateIso);
  const datePart = formatOrderDate_DatePart(orderDateIso);

  return `${timePart}, ${datePart}`;
};

export const formatOrderDate_DatePart = (isoDate?: string): string => {
  if (!isoDate) {
    return '';
  }

  const [date, _] = isoDate.split('T');
  const monthNumber = Number(date.slice(5, 7));

  const month = monthNames[monthNumber - 1];
  const day = Number(date.slice(8, 10));
  const year = date.slice(0, 4);

  const daysuffix: string = dateSuffixMap[day] || 'th';

  return `${day}${daysuffix} ${month} ${year}`;
};

export const formatOrderDate_TimePart = (isoDate?: string): string => {
  if (!isoDate) {
    return '';
  }

  const [_, time] = isoDate.split('T');
  const hourAndMinutes = time.slice(0, 5);

  return hourAndMinutes;
};

export const formatStartDate = (
  startDate?: string,
  fare?: ITicketDescriptionEntry
): string => {
  if (fare && !fare.travelMethod) {
    //identify Flexi/PAYG as having no travel method specified
    return '';
  }

  if (fare && fare.termDateFrom) {
    return formatOrderDate_DatePart(fare.termDateFrom);
  }

  if (!startDate) {
    return '';
  }
  return formatOrderDate_DatePart(startDate);
};

export const formatEndDate = (
  startDate?: string,
  fare?: ITicketDescriptionEntry
): string => {
  if (!fare || !fare.travelMethod) {
    //identify Flexi/PAYG as having no travel method specified
    return '';
  }

  if (fare.termDateTo) {
    return formatOrderDate_DatePart(fare.termDateTo);
  }

  if (!startDate) {
    return '';
  }

  const newDate = addDaysToDate(new Date(startDate), fare.noDays - 1);
  return formatOrderDate_DatePart(newDate.toISOString());
};
